import { useState, useRef, useCallback, useEffect } from "react";
import { Message, User } from "../types";
import { uploadFile } from "../axios/uploadFiles";
import useAppStore from "../stores/appStore";
import { sendRecordingEvent } from "../axios/realtimeSpeech";
import {
  playStartRecordingSound,
  playStopRecordingSound,
  playSendSound,
  playCancelSound,
  playHapticFeedback,
} from "../components/chat/whatsapp-audio-recorder/audio-effects";

interface UseWhatsAppAudioRecorderProps {
  sendMessage: React.Dispatch<React.SetStateAction<Message[]>>;
  sendMessageSocket: (message: Message) => void;
  currentUser: User;
  scrollToBottom?: () => void;
}

interface AudioRecorderState {
  isRecording: boolean;
  isPaused: boolean;
  isProcessing: boolean;
  isPlaying: boolean;
  recordTime: number;
  recordTimeMs: number;
  audioDuration: number;
  currentTime: number;
  recordedAudioBlob: Blob | null;
  recordedAudioUrl: string | null;
  pausedPreviewUrl: string | null;
}

const getBlobType = (recorderMimeType?: string): string => {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isSafari || isIOS) return "audio/mp3";
  return recorderMimeType || "audio/webm";
};

export const useWhatsAppAudioRecorder = ({
  sendMessage,
  sendMessageSocket: _sendMessageSocket,
  currentUser,
  scrollToBottom,
}: UseWhatsAppAudioRecorderProps) => {
  const { focus, domain } = useAppStore();

  const [state, setState] = useState<AudioRecorderState>({
    isRecording: false,
    isPaused: false,
    isProcessing: false,
    isPlaying: false,
    recordTime: 0,
    recordTimeMs: 0,
    audioDuration: 0,
    currentTime: 0,
    recordedAudioBlob: null,
    recordedAudioUrl: null,
    pausedPreviewUrl: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const startRecordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isInitializedRef = useRef(false);
  const isPausedRef = useRef(false);
  const cancelledRef = useRef(false);
  const accumulatedMsRef = useRef(0);
  const segmentStartedAtRef = useRef<number | null>(null);
  const pausedPreviewUrlRef = useRef<string | null>(null);
  const recordedAudioUrlRef = useRef<string | null>(null);

  const getMimeType = useCallback((): string => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const mimeTypes =
      isSafari || isIOS
        ? [
            "audio/mp4",
            "audio/mp4;codecs=mp4a.40.2",
            "audio/aac",
            "audio/webm;codecs=opus",
            "audio/webm",
            "audio/ogg;codecs=opus",
            "audio/ogg",
          ]
        : [
            "audio/webm;codecs=opus",
            "audio/webm",
            "audio/mp4;codecs=mp4a.40.2",
            "audio/mp4",
            "audio/ogg;codecs=opus",
            "audio/ogg",
          ];

    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return "";
  }, []);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (startRecordingTimeoutRef.current) {
      clearTimeout(startRecordingTimeoutRef.current);
      startRecordingTimeoutRef.current = null;
    }
  }, []);

  const revokePausedPreview = useCallback(() => {
    if (pausedPreviewUrlRef.current) {
      URL.revokeObjectURL(pausedPreviewUrlRef.current);
      pausedPreviewUrlRef.current = null;
    }
    setState((prev) =>
      prev.pausedPreviewUrl ? { ...prev, pausedPreviewUrl: null } : prev,
    );
  }, []);

  const revokeRecordedUrl = useCallback(() => {
    if (recordedAudioUrlRef.current) {
      URL.revokeObjectURL(recordedAudioUrlRef.current);
      recordedAudioUrlRef.current = null;
    }
  }, []);

  const syncTimerDisplay = useCallback(() => {
    const live =
      segmentStartedAtRef.current != null && !isPausedRef.current
        ? Date.now() - segmentStartedAtRef.current
        : 0;
    const total = accumulatedMsRef.current + live;
    setState((prev) => ({
      ...prev,
      recordTimeMs: total,
      recordTime: Math.floor(total / 1000),
    }));
  }, []);

  const startRecordTimer = useCallback(
    (reset: boolean) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (reset) {
        accumulatedMsRef.current = 0;
      }
      segmentStartedAtRef.current = Date.now();
      syncTimerDisplay();
      timerRef.current = setInterval(syncTimerDisplay, 50);
    },
    [syncTimerDisplay],
  );

  const pauseRecordTimer = useCallback(() => {
    if (segmentStartedAtRef.current != null) {
      accumulatedMsRef.current += Date.now() - segmentStartedAtRef.current;
      segmentStartedAtRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    syncTimerDisplay();
  }, [syncTimerDisplay]);

  const buildPreviewFromChunks = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || audioChunksRef.current.length === 0) return null;
    const blob = new Blob(audioChunksRef.current, {
      type: getBlobType(recorder.mimeType),
    });
    if (blob.size === 0) return null;
    return URL.createObjectURL(blob);
  }, []);

  const cleanup = useCallback(() => {
    clearTimers();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    revokePausedPreview();
    revokeRecordedUrl();
    audioChunksRef.current = [];
    accumulatedMsRef.current = 0;
    segmentStartedAtRef.current = null;
    isPausedRef.current = false;
    isInitializedRef.current = false;
  }, [clearTimers, revokePausedPreview, revokeRecordedUrl]);

  const startRecording = useCallback(async () => {
    try {
      if (isInitializedRef.current) {
        console.warn("Recording already initialized");
        return;
      }

      isInitializedRef.current = true;
      cancelledRef.current = false;
      revokeRecordedUrl();
      revokePausedPreview();

      playStartRecordingSound();
      playHapticFeedback();

      const streamPromise = navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        startRecordingTimeoutRef.current = setTimeout(() => {
          reject(new Error("getUserMedia timeout"));
        }, 10000);
      });

      const stream = await Promise.race([streamPromise, timeoutPromise]);

      if (startRecordingTimeoutRef.current) {
        clearTimeout(startRecordingTimeoutRef.current);
        startRecordingTimeoutRef.current = null;
      }

      streamRef.current = stream;

      const mimeType = getMimeType();
      const options: MediaRecorderOptions = {};
      if (mimeType) {
        options.mimeType = mimeType;
      }

      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setState((prev) => ({
          ...prev,
          isRecording: false,
          isPaused: false,
          isProcessing: false,
        }));
        cleanup();
      };

      mediaRecorderRef.current.onstop = () => {
        const wasCancelled = cancelledRef.current;
        cancelledRef.current = false;

        clearTimers();
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        revokePausedPreview();
        isPausedRef.current = false;
        isInitializedRef.current = false;

        if (wasCancelled) {
          audioChunksRef.current = [];
          setState((prev) => ({
            ...prev,
            isRecording: false,
            isPaused: false,
            isProcessing: false,
            recordedAudioBlob: null,
            recordedAudioUrl: null,
            recordTime: 0,
            recordTimeMs: 0,
          }));
          return;
        }

        const blob = new Blob(audioChunksRef.current, {
          type: getBlobType(mediaRecorderRef.current?.mimeType || mimeType),
        });

        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          recordedAudioUrlRef.current = url;
          setState((prev) => ({
            ...prev,
            recordedAudioBlob: blob,
            recordedAudioUrl: url,
            isRecording: false,
            isPaused: false,
            isProcessing: false,
            audioDuration: prev.recordTimeMs / 1000,
          }));
        } else {
          console.error("Audio blob is empty");
          setState((prev) => ({
            ...prev,
            isRecording: false,
            isPaused: false,
            isProcessing: false,
          }));
        }
      };

      mediaRecorderRef.current.start(250);

      setState((prev) => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        isProcessing: false,
        recordTime: 0,
        recordTimeMs: 0,
        recordedAudioBlob: null,
        recordedAudioUrl: null,
      }));

      recordingStartTimeRef.current = Date.now();
      startRecordTimer(true);

      try {
        await sendRecordingEvent("start", 0);
      } catch (error) {
        console.error("Failed to send recording start event:", error);
      }

      recordingIntervalRef.current = setInterval(async () => {
        const elapsedSeconds = Math.floor(
          (Date.now() - recordingStartTimeRef.current) / 1000,
        );
        try {
          await sendRecordingEvent("recording", elapsedSeconds);
        } catch (error) {
          console.error("Failed to send recording progress event:", error);
        }
      }, 5000);
    } catch (error) {
      console.error("Error starting recording:", error);

      cleanup();

      setState((prev) => ({
        ...prev,
        isRecording: false,
        isPaused: false,
        isProcessing: false,
      }));

      isInitializedRef.current = false;
    }
  }, [
    cleanup,
    clearTimers,
    getMimeType,
    revokePausedPreview,
    revokeRecordedUrl,
    startRecordTimer,
  ]);

  const stopRecording = useCallback(() => {
    const recorderState = mediaRecorderRef.current?.state;
    if (recorderState !== "recording" && recorderState !== "paused") {
      return;
    }

    pauseRecordTimer();
    setState((prev) => ({
      ...prev,
      isRecording: false,
      isPaused: false,
      isProcessing: true,
    }));

    playStopRecordingSound();
    playHapticFeedback();
    mediaRecorderRef.current?.stop();
  }, [pauseRecordTimer]);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    try {
      recorder.requestData();
    } catch {
      // ignore
    }
    recorder.pause();
    pauseRecordTimer();
    isPausedRef.current = true;
    setState((prev) => ({ ...prev, isPaused: true }));
    playHapticFeedback();

    window.setTimeout(() => {
      if (!isPausedRef.current) return;
      revokePausedPreview();
      const previewUrl = buildPreviewFromChunks();
      if (previewUrl) {
        pausedPreviewUrlRef.current = previewUrl;
        setState((prev) => ({ ...prev, pausedPreviewUrl: previewUrl }));
      }
    }, 80);
  }, [buildPreviewFromChunks, pauseRecordTimer, revokePausedPreview]);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "paused") return;

    revokePausedPreview();
    recorder.resume();
    isPausedRef.current = false;
    setState((prev) => ({ ...prev, isPaused: false }));
    startRecordTimer(false);
    playHapticFeedback();
  }, [revokePausedPreview, startRecordTimer]);

  const cancelRecording = useCallback(async () => {
    const elapsedSeconds = Math.floor(
      (Date.now() - recordingStartTimeRef.current) / 1000,
    );

    try {
      await sendRecordingEvent("discard", elapsedSeconds);
    } catch (error) {
      console.error("Failed to send recording discard event:", error);
    }

    cancelledRef.current = true;
    const recorderState = mediaRecorderRef.current?.state;
    if (recorderState === "recording" || recorderState === "paused") {
      try {
        mediaRecorderRef.current?.stop();
      } catch {
        // ignore
      }
    } else {
      cleanup();
      setState({
        isRecording: false,
        isPaused: false,
        isProcessing: false,
        isPlaying: false,
        recordTime: 0,
        recordTimeMs: 0,
        audioDuration: 0,
        currentTime: 0,
        recordedAudioBlob: null,
        recordedAudioUrl: null,
        pausedPreviewUrl: null,
      });
    }

    playCancelSound();
    playHapticFeedback();
  }, [cleanup]);

  const togglePlayback = useCallback(async () => {
    if (!audioRef.current || !state.recordedAudioUrl) return;

    try {
      if (state.isPlaying) {
        audioRef.current.pause();
        setState((prev) => ({ ...prev, isPlaying: false }));
      } else {
        await audioRef.current.play();
        setState((prev) => ({ ...prev, isPlaying: true }));
      }
    } catch (error) {
      console.error("Audio playback error:", error);
      setState((prev) => ({ ...prev, isPlaying: false }));
    }
  }, [state.isPlaying, state.recordedAudioUrl]);

  const handleAudioEnded = useCallback(() => {
    setState((prev) => ({ ...prev, isPlaying: false, currentTime: 0 }));
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setState((prev) => ({
        ...prev,
        currentTime: audioRef.current?.currentTime || 0,
      }));
    }
  }, []);

  const handleAudioLoaded = useCallback(() => {
    if (!audioRef.current) return;

    const setDuration = () => {
      if (
        audioRef.current &&
        isFinite(audioRef.current.duration) &&
        audioRef.current.duration > 0
      ) {
        setState((prev) => ({
          ...prev,
          audioDuration: audioRef.current!.duration,
        }));
      } else if (audioRef.current && state.recordTime > 0) {
        setState((prev) => ({
          ...prev,
          audioDuration: state.recordTime,
        }));
      }
    };

    setDuration();

    setTimeout(() => {
      if (
        audioRef.current &&
        (!isFinite(audioRef.current.duration) ||
          audioRef.current.duration === 0)
      ) {
        setDuration();
      }
    }, 100);
  }, [state.recordTime]);

  const handleCanPlayThrough = useCallback(() => {
    if (
      audioRef.current &&
      isFinite(audioRef.current.duration) &&
      audioRef.current.duration > 0
    ) {
      setState((prev) => ({
        ...prev,
        audioDuration: audioRef.current!.duration,
      }));
    }
  }, []);

  const handleAudioError = useCallback(() => {
    console.error("Audio playback error - using fallback duration");
    if (state.recordTime > 0) {
      setState((prev) => ({
        ...prev,
        audioDuration: state.recordTime,
      }));
    }
  }, [state.recordTime]);

  const sendRecordedAudio = useCallback(async () => {
    if (!state.recordedAudioBlob) return;

    try {
      setState((prev) => ({ ...prev, isProcessing: true }));

      const audioResponse = await uploadFile(state.recordedAudioBlob, "audio");
      const finalURL = audioResponse.file_url;

      const audioMessage: Message = {
        message_id: audioResponse.message_id,
        from_user: currentUser,
        text: "",
        file_url: finalURL,
        type: "audio",
        is_bot: false,
        date: new Date().toISOString(),
        chat: {
          id: currentUser.id,
          type: "private",
        },
        user_id: currentUser.id,
        message_type: "default",
        domain: domain,
        focus: focus,
        play_gif_once: true,
      };

      sendMessage((prev) => [...prev, audioMessage]);

      if (scrollToBottom) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }

      cleanup();

      setState({
        isRecording: false,
        isPaused: false,
        isProcessing: false,
        isPlaying: false,
        recordTime: 0,
        recordTimeMs: 0,
        audioDuration: 0,
        currentTime: 0,
        recordedAudioBlob: null,
        recordedAudioUrl: null,
        pausedPreviewUrl: null,
      });

      playSendSound();
      playHapticFeedback();
    } catch (error) {
      console.error("Failed to upload audio:", error);

      cleanup();

      setState((prev) => ({
        ...prev,
        isProcessing: false,
        recordedAudioBlob: null,
        recordedAudioUrl: null,
      }));
    }
  }, [
    state.recordedAudioBlob,
    currentUser,
    domain,
    focus,
    sendMessage,
    cleanup,
    scrollToBottom,
  ]);

  const formatTime = useCallback((seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return "0:00";
    }

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    ...state,

    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    togglePlayback,
    sendRecordedAudio,

    formatTime,
    handleAudioLoaded,
    handleAudioEnded,
    handleTimeUpdate,
    handleCanPlayThrough,
    handleAudioError,

    audioRef,
  };
};
